import { money } from '../lib/money';

// How a search hit reads, in ONE place for the bar and the results page (Leo's wording): titles, descriptors, amounts, dates.
// Pure functions; `t` is the i18n function of the current language.

export const MIN_QUERY = 3;

const locale = (lang) => (lang === 'it' ? 'it-IT' : 'en-GB');

// "12 Aug" (the year only when it is not this year).
export function shortDate(iso, lang, now = new Date()) {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const opts = { day: 'numeric', month: 'short' };
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString(locale(lang), opts);
}

export const docTypeLabel = (t, type) => t(`search.doc.${type}`);

const refOfCase = (t, c) => (c.po_number ? t('search.po', { n: c.po_number }) : c.invoice_number ? t('search.invoiceRef', { n: c.invoice_number }) : null);

// "PO 198664 · Mobus Fabrics → Architex"  (no buyer on the invoice: no arrow, nothing invented)
export function caseTitle(t, c) {
  const head = [refOfCase(t, c), c.supplier].filter(Boolean).join(' · ');
  return c.buyer ? `${head} → ${c.buyer}` : head;
}

// "PO 198664 · needs attention"
export function caseSub(t, c) {
  return [refOfCase(t, c), c.chip ? t(`search.chip.${c.chip}`) : null].filter(Boolean).join(' · ');
}

export const caseAmount = (c) => (c.amount != null ? `+${money(c.amount, c.currency)}` : null);

// "Purchase order 198664"
export const documentTitle = (t, d) => [docTypeLabel(t, d.type), d.number].filter(Boolean).join(' ');

// "Architex International · 12 Aug"  or  "Mobus · cites 198664"
export function documentSub(t, lang, d) {
  const tail = d.cites ? t('search.cites', { n: d.cites }) : shortDate(d.date, lang);
  return [d.company, tail].filter(Boolean).join(' · ');
}

export const companyRole = (t, c) => t(c.role === 'customer' ? 'search.customer' : 'search.supplier');

// "Supplier · 64 cases" (suppliers only; a customer has no count: cases have no customer link); an archived supplier is marked "Archived" (Leo D27)
export function companySub(t, c) {
  const archived = c.archived ? t('search.archived') : null;
  if (c.role !== 'supplier' || c.cases == null) return [companyRole(t, c), archived].filter(Boolean).join(' · ');
  return [companyRole(t, c), c.cases === 1 ? t('search.case.one') : t('search.case.many', { n: c.cases }), archived].filter(Boolean).join(' · ');
}

// Where a hit leads: the case page, the supplier page, or (a document with no case) its original file. A customer has no page yet.
export function hitTarget(r) {
  if (r.kind === 'case') return { to: `/cases/${r.id}` };
  if (r.kind === 'document') return r.case_id ? { to: `/cases/${r.case_id}` } : r.has_file ? { file: r.id } : {};
  if (r.kind === 'company') return r.role === 'supplier' ? { to: `/suppliers/${r.id}` } : {};
  return {};
}

// The results page address never carries the term (it would end up in browser history and in access logs): the term travels in the
// router's in-memory state, see SearchBar and SearchResults.
export const RESULTS_PATH = '/search';

// "9 matches across cases, documents and parties" ("1,007+" when a group is capped)
export function matchesLine(t, data) {
  const g = data.groups;
  const capped = g.cases.capped || g.documents.capped || g.companies.capped;
  const n = data.matches.toLocaleString('en-GB') + (capped ? '+' : '');
  return data.matches === 1 && !capped ? t('search.count.one') : t('search.count', { n });
}
